import { classifySportForAnalysisTab } from "./analysisDataMaps";

const ANALYSIS_DEFAULT_LANG = "en-GB";

const MS_PER_DAY = 86400000;

const MAX_LIVE_MINUTE = 150;
const MAX_LIVE_STOPPAGE = 20;

export function isSuspiciousEpochLikeMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return true;
  if (ms <= 0) return true;
  if (ms < MS_PER_DAY) return true;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return true;
  if (d.getUTCFullYear() <= 1971) return true;
  return false;
}

export function isSuspiciousEpochLikeDate(date) {
  if (!(date instanceof Date)) return true;
  const ms = date.getTime();
  if (Number.isNaN(ms)) return true;
  return isSuspiciousEpochLikeMs(ms);
}

export function isSuspiciousEpochLikeInput(value) {
  if (value == null || value === "") return true;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return true;
    const ms = coerceNumericEpochMs(value);
    if (ms == null) return true;
    return isSuspiciousEpochLikeMs(ms);
  }
  if (value instanceof Date) {
    return isSuspiciousEpochLikeDate(value);
  }
  const parsed = parseAnalysisInstant(value);
  if (!parsed) return true;
  return isSuspiciousEpochLikeDate(parsed);
}

export function coerceNumericEpochMs(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  if (n <= 0) return null;
  const a = Math.abs(n);
  if (a >= 1e12) return n;
  if (a >= 1e9) return Math.round(n * 1000);
  return null;
}

function isSuspiciousKickoffDisplayString(s) {
  const t = String(s || "").trim();
  if (!t) return true;
  if (/1970|01\s*Jan|Jan\s*01|01\/01\/1970|1970-01-01|01\.01\.1970/i.test(t)) return true;
  return false;
}

function isTimeOnlyDisplayString(s) {
  const t = String(s || "").trim();
  if (!t) return false;

  // Reject time-only strings like "07:00" (or "07:00Z") so they don't get parsed
  // into a "real" Date on today's date (which would then be Intl-formatted).
  // This forces fallback to the raw kickoff label when there is no real instant.
  const timeOnlyRegex =
    /^\d{1,2}:\d{2}(:\d{2})?(\s*(AM|PM))?(\s*(Z|[+-]\d{2}:?\d{2}))?$/i;
  return timeOnlyRegex.test(t);
}

export function parseAnalysisInstant(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return isSuspiciousEpochLikeDate(value) ? null : value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = coerceNumericEpochMs(value);
    if (ms == null) return null;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return isSuspiciousEpochLikeDate(d) ? null : d;
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return null;
    if (isTimeOnlyDisplayString(t)) return null;
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      const num = Number(t);
      if (Number.isFinite(num)) return parseAnalysisInstant(num);
    }
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return null;
    return isSuspiciousEpochLikeDate(d) ? null : d;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return isSuspiciousEpochLikeDate(d) ? null : d;
}

function safeDate(value) {
  return parseAnalysisInstant(value);
}

function normalizeLang(lang) {
  return lang || ANALYSIS_DEFAULT_LANG;
}

function normalizeTimeZone(timeZone) {
  if (typeof timeZone !== "string") return undefined;
  const value = timeZone.trim();
  return value || undefined;
}

function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function getAnalysisTimeZone(item, explicitTimeZone) {
  const direct = normalizeTimeZone(explicitTimeZone);
  if (direct) return direct;

  const fx = item?.fixture || {};
  const ev = item?.event || fx.event || {};
  const tz =
    item?.timeZone ||
    item?.timezone ||
    item?.tz ||
    fx?.timezone ||
    fx?.timeZone ||
    fx?.tz ||
    ev?.timeZone ||
    ev?.timezone ||
    ev?.tz ||
    item?.userTimeZone ||
    item?.userTimezone ||
    item?.accountTimeZone ||
    item?.accountTimezone ||
    item?.profileTimeZone ||
    item?.profileTimezone ||
    "";

  const itemTz = normalizeTimeZone(tz);
  if (itemTz) return itemTz;

  return getBrowserTimeZone();
}

function getFormatterOptions(timeZone, extra = {}) {
  return timeZone ? { ...extra, timeZone } : extra;
}

function formatTimeWithLang(date, lang = ANALYSIS_DEFAULT_LANG, timeZone) {
  try {
    return new Intl.DateTimeFormat(
      normalizeLang(lang),
      getFormatterOptions(timeZone, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      })
    ).format(date);
  } catch {
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }
}

function formatDateWithLang(date, lang = ANALYSIS_DEFAULT_LANG, timeZone) {
  try {
    return new Intl.DateTimeFormat(
      normalizeLang(lang),
      getFormatterOptions(timeZone, {
        day: "2-digit",
        month: "short"
      })
    ).format(date);
  } catch {
    return date.toLocaleDateString(normalizeLang(lang), {
      day: "2-digit",
      month: "short"
    });
  }
}

export function getItemDate(item) {
  const fx = item?.fixture || {};
  const ev = item?.event || fx.event || {};
  const candidates = [
    item?.kickoffTs,
    item?.kickoffDate,
    item?.kickoff,
    fx?.kickoffTs,
    fx?.kickoffDate,
    fx?.kickoff,
    fx?.date,
    fx?.startsAt,
    fx?.startTime,
    fx?.scheduled,
    ev?.dateTime,
    ev?.datetime,
    ev?.startsAt,
    ev?.startTime,
    ev?.scheduled,
    item?.timestamp,
    item?.ts,
    item?.dateTime,
    item?.datetime,
    item?.kickoffAt,
    item?.kickoffTime,
    item?.kickoffDateTime,
    item?.startTime,
    item?.startDateTime,
    item?.eventTime,
    item?.matchTime,
    item?.scheduled,
    item?.date,
    item?.matchDate,
    item?.eventDate,
    item?.startDate
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (v == null || v === "") continue;
    if (typeof v === "number") {
      const ms = coerceNumericEpochMs(v);
      if (ms == null || isSuspiciousEpochLikeMs(ms)) continue;
      return v;
    }
    const parsed = parseAnalysisInstant(v);
    if (!parsed || isSuspiciousEpochLikeDate(parsed)) continue;
    return v;
  }
  return null;
}

function getKickoffDisplay(
  item,
  lang = ANALYSIS_DEFAULT_LANG,
  fallbackKickoff = "—",
  explicitTimeZone
) {
  const rawKickoff =
    item?.kickoff ??
    item?.fixture?.kickoff ??
    item?.time ??
    item?.matchTimeLabel ??
    item?.startTimeLabel ??
    item?.eventTimeLabel ??
    "";

  const rawDate = getItemDate(item);
  let dt = null;
  if (rawDate != null && rawDate !== "") {
    dt = safeDate(rawDate);
  }
  const timeZone = getAnalysisTimeZone(item, explicitTimeZone);

  if (dt && !isSuspiciousEpochLikeDate(dt)) {
    return formatTimeWithLang(dt, lang, timeZone);
  }

  if (rawKickoff) {
    const s = String(rawKickoff);
    if (!isSuspiciousKickoffDisplayString(s)) return s;
  }

  return fallbackKickoff;
}

export function formatAnalysisTime(ts, lang = ANALYSIS_DEFAULT_LANG, timeZone) {
  if (ts == null || ts === "") return "—";

  const resolvedTimeZone = getAnalysisTimeZone(undefined, timeZone);
  const d = parseAnalysisInstant(ts);
  if (!d) {
    // Preserve honest fallback: if we can't parse a real instant, only show raw
    // time labels (e.g. "07:00") when they look like kickoff-time strings.
    if (isTimeOnlyDisplayString(ts) && !isSuspiciousKickoffDisplayString(String(ts))) {
      return String(ts).trim();
    }
    return "—";
  }
  try {
    return formatTimeWithLang(d, lang, resolvedTimeZone);
  } catch {
    return "—";
  }
}

export function formatAnalysisPrematchDate(
  item,
  lang = ANALYSIS_DEFAULT_LANG,
  explicitTimeZone
) {
  const rawDate = getItemDate(item);

  if (rawDate != null && rawDate !== "") {
    const dt = safeDate(rawDate);
    if (dt && !isSuspiciousEpochLikeDate(dt)) {
      return formatDateWithLang(dt, lang, getAnalysisTimeZone(item, explicitTimeZone));
    }
  }

  return "—";
}

function isPlausibleLiveMinuteInt(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return false;
  if (n !== Math.floor(n)) return false;
  return n >= 0 && n <= MAX_LIVE_MINUTE;
}

function isPlausibleStoppageInt(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return false;
  if (n !== Math.floor(n)) return false;
  return n >= 0 && n <= MAX_LIVE_STOPPAGE;
}

function formatLiveClockFromParts(mainMinute, stoppage) {
  if (!isPlausibleLiveMinuteInt(mainMinute)) return null;
  const add =
    stoppage != null && isPlausibleStoppageInt(stoppage) && stoppage > 0 ? stoppage : 0;
  if (add > 0) return `${mainMinute}+${add}'`;
  return `${mainMinute}'`;
}

function tryParseMmSsMatchClock(s) {
  const t = String(s || "").trim();
  const m = /^(\d{1,3}):(\d{2})$/.exec(t);
  if (!m) return null;
  const mm = parseInt(m[1], 10);
  const ss = parseInt(m[2], 10);
  if (!isPlausibleLiveMinuteInt(mm)) return null;
  if (ss < 0 || ss > 59) return null;
  // Reject ambiguous HH:MM wall-clock times (e.g. 20:00 kickoff).
  if (mm <= 23) return null;
  return `${mm}'`;
}

function parseLiveClockLabel(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (isPlausibleLiveMinuteInt(raw)) return `${raw}'`;
    return null;
  }
  const s = String(raw).trim();
  if (!s) return null;
  if (isTimeOnlyDisplayString(s)) return null;

  const directPlus = /^\s*(\d{1,3})\s*\+\s*(\d{1,2})\s*['′´`]?\s*$/i.exec(s);
  if (directPlus) {
    const main = parseInt(directPlus[1], 10);
    const add = parseInt(directPlus[2], 10);
    if (isPlausibleLiveMinuteInt(main) && add >= 1 && add <= MAX_LIVE_STOPPAGE) {
      return `${main}+${add}'`;
    }
    return null;
  }

  const plainAp = /^\s*(\d{1,3})\s*['′´`]\s*$/i.exec(s);
  if (plainAp && !s.includes("+")) {
    const main = parseInt(plainAp[1], 10);
    if (isPlausibleLiveMinuteInt(main)) return `${main}'`;
    return null;
  }

  const plainNum = /^\s*(\d{1,3})\s*$/i.exec(s);
  if (plainNum && !s.includes("+") && !s.includes(":")) {
    const main = parseInt(plainNum[1], 10);
    if (isPlausibleLiveMinuteInt(main)) return `${main}'`;
    return null;
  }

  const embeddedPlus = /(?:^|[\s:•·|/\\-])(\d{1,3})\s*\+\s*(\d{1,2})\s*['′´`]?(?=\s|$|[,'"•·|])/i.exec(
    s
  );
  if (embeddedPlus) {
    const main = parseInt(embeddedPlus[1], 10);
    const add = parseInt(embeddedPlus[2], 10);
    if (isPlausibleLiveMinuteInt(main) && add >= 1 && add <= MAX_LIVE_STOPPAGE) {
      return `${main}+${add}'`;
    }
  }

  const embeddedAp = /(?:^|[\s:•·|/\\-])(\d{1,3})\s*['′´`](?=\s|$|[,'"•·|])/i.exec(s);
  if (embeddedAp && !/\d{1,3}\s*\+/.test(s)) {
    const main = parseInt(embeddedAp[1], 10);
    if (isPlausibleLiveMinuteInt(main)) return `${main}'`;
  }

  return null;
}

function tryStoppageFromUnknown(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!isPlausibleStoppageInt(n)) return null;
  return n;
}

function tryMinuteFromUnknown(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) {
    if (isPlausibleLiveMinuteInt(v)) return v;
    return null;
  }
  if (typeof v === "string") {
    const t = v.trim();
    if (!t || isTimeOnlyDisplayString(t)) return null;
    if (/^\d{1,3}$/.test(t)) {
      const n = parseInt(t, 10);
      if (isPlausibleLiveMinuteInt(n)) return n;
    }
  }
  return null;
}

function gatherNestedObjects(item) {
  const fx = item?.fixture || {};
  const ev = item?.event || fx.event || {};
  const sb = item?.scoreboard ?? fx?.scoreboard ?? ev?.scoreboard;
  const live = item?.live ?? fx?.live ?? ev?.live;
  const timer = item?.timer ?? fx?.timer ?? ev?.timer;
  const period = item?.period ?? fx?.period ?? ev?.period;
  return { fx, ev, sb, live, timer, period };
}

function pushStatusToken(parts, v) {
  if (v == null || v === "") return;
  if (typeof v === "object") return;
  const s = String(v).trim();
  if (s) parts.push(s);
}

function gatherMarketScopeText(item, norm) {
  const parts = [];
  const push = (v) => {
    if (v == null || v === "") return;
    if (typeof v === "object") return;
    const s = String(v).trim();
    if (s) parts.push(s);
  };

  push(item?.market);
  push(item?.marketType);
  push(item?.market_type);
  push(item?.marketName);
  push(item?.marketLabel);
  push(item?.marketDisplay);
  push(item?.marketRowLabel);
  push(item?.lineLabel);

  // Some normalized rows include scope/period hints as display text already.
  push(item?.scope);
  push(item?.period);
  push(item?.currentPeriod);
  push(item?.current_period);

  // Include status tokens (already curated to strings).
  if (norm?.statusParts && Array.isArray(norm.statusParts)) {
    for (let i = 0; i < norm.statusParts.length; i += 1) push(norm.statusParts[i]);
  }

  return parts.join(" ").toLowerCase();
}

function inferTennisSetNumberFromMarketScopeText(item, norm) {
  const s = gatherMarketScopeText(item, norm);
  if (!s) return null;

  // Only allow Set 1-3 from market scope text (conservative).
  if (/\bset\s*1\b|\b1st\s*set\b|\bfirst\s*set\b/i.test(s)) return 1;
  if (/\bset\s*2\b|\b2nd\s*set\b|\bsecond\s*set\b/i.test(s)) return 2;
  if (/\bset\s*3\b|\b3rd\s*set\b|\bthird\s*set\b/i.test(s)) return 3;
  return null;
}

function inferBasketballQuarterFromMarketScopeText(item, norm) {
  const s = gatherMarketScopeText(item, norm);
  if (!s) return null;

  if (/\b(?:overtime|\bot\b|ot\s*\d)\b/i.test(s)) return "OT";
  if (/\b(?:half[\s-]?time|halftime)\b/i.test(s)) return "HT";

  if (/\bq\s*1\b|\b1st\s*quarter\b|\bfirst\s*quarter\b/i.test(s)) return 1;
  if (/\bq\s*2\b|\b2nd\s*quarter\b|\bsecond\s*quarter\b/i.test(s)) return 2;
  if (/\bq\s*3\b|\b3rd\s*quarter\b|\bthird\s*quarter\b/i.test(s)) return 3;
  if (/\bq\s*4\b|\b4th\s*quarter\b|\bfourth\s*quarter\b/i.test(s)) return 4;

  return null;
}

function inferEsportsMapNumberFromMarketScopeText(item, norm) {
  const s = gatherMarketScopeText(item, norm);
  if (!s) return null;

  const m = /\b(?:map|game)\s*#?\s*(\d{1,2})\b/i.exec(s);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 99) return n;
  }
  return null;
}

/**
 * Aggregates raw live-state from common Sharp/UI shapes for sport-aware formatting.
 */
function readNormalizedLiveState(item) {
  const { fx, ev, sb, live, timer, period } = gatherNestedObjects(item);
  const gs = item?.game_state ?? fx?.game_state ?? ev?.game_state ?? live?.game_state;

  const statusParts = [];

  pushStatusToken(statusParts, item?.status);
  pushStatusToken(statusParts, item?.matchStatus);
  pushStatusToken(statusParts, item?.state);
  pushStatusToken(statusParts, item?.phase);
  pushStatusToken(statusParts, item?.statusLabel);
  pushStatusToken(statusParts, item?.matchStatusLabel);
  pushStatusToken(statusParts, item?.status_text);
  pushStatusToken(statusParts, item?.statusText);
  pushStatusToken(statusParts, item?.gameStatus);
  pushStatusToken(statusParts, item?.game_status);
  pushStatusToken(statusParts, item?.scope);
  pushStatusToken(statusParts, fx?.status);
  pushStatusToken(statusParts, ev?.status);
  pushStatusToken(statusParts, fx?.statusLabel);
  pushStatusToken(statusParts, ev?.statusLabel);
  pushStatusToken(statusParts, live?.status);
  pushStatusToken(statusParts, sb?.status);
  pushStatusToken(statusParts, timer?.status);
  if (period && typeof period === "object") {
    pushStatusToken(statusParts, period.name);
    pushStatusToken(statusParts, period.type);
    pushStatusToken(statusParts, period.label);
    pushStatusToken(statusParts, period.code);
  }
  if (typeof gs === "string") {
    pushStatusToken(statusParts, gs);
  } else if (gs && typeof gs === "object") {
    pushStatusToken(statusParts, gs.status);
    pushStatusToken(statusParts, gs.phase);
    pushStatusToken(statusParts, gs.period);
    pushStatusToken(statusParts, gs.periodName);
    pushStatusToken(statusParts, gs.matchStatus);
    pushStatusToken(statusParts, gs.state);
    pushStatusToken(statusParts, gs.clock);
  }

  const statusBlob = statusParts.join(" ").toLowerCase();

  const periodObj = period && typeof period === "object" ? period : null;
  const periodPrimitive =
    typeof period === "number" && Number.isFinite(period) ? period : null;

  return {
    statusBlob,
    statusParts,
    fx,
    ev,
    sb,
    live,
    timer,
    periodObj,
    periodPrimitive,
    itemTopQuarter: item?.quarter ?? item?.currentQuarter ?? item?.current_quarter,
    itemTopClock:
      item?.clock ??
      item?.matchClock ??
      item?.match_clock ??
      item?.gameClock ??
      item?.game_clock ??
      item?.timeRemaining ??
      item?.time_remaining,
    gs
  };
}

function analysisSportTabForItem(item) {
  const raw =
    item?.sport ??
    item?.sportKey ??
    item?.sportName ??
    item?.category ??
    item?.gameType ??
    "";
  const match =
    item?.match ??
    item?.matchName ??
    item?.eventName ??
    item?.event_name ??
    "";
  return classifySportForAnalysisTab(raw, { league: item?.league, match });
}

function isClearlyFinishedForSport(statusBlob, sportTab) {
  if (!statusBlob) return false;
  const s = statusBlob;
  if (/\b(?:cancelled|canceled|abandon|postponed|void)\b/i.test(s)) return false;

  if (sportTab === "football") {
    return (
      /\b(?:full[\s-]?time|fulltime|match\s*ended|after\s*(?:extra|penalt)|aet)\b/i.test(s) ||
      /\b(?:penalt(?:y|ies)\s*shootout\s*finished|shootout\s*complete)\b/i.test(s) ||
      (/\b(?:finished|ended|complete)\b/i.test(s) && !/\b(?:not\s*started|scheduled)\b/i.test(s)) ||
      /(?:^|[\s,])(?:ft|aet)(?:[\s,]|$)/i.test(s) ||
      /\b(?:game\s*ended|final\s*whistle)\b/i.test(s)
    );
  }

  if (sportTab === "basketball") {
    return (
      /\b(?:final|game\s*set|end\s*of\s*game)\b/i.test(s) ||
      (/\b(?:finished|complete)\b/i.test(s) && /\b(?:nba|wnba|ncaab|game)\b/i.test(s))
    );
  }

  if (sportTab === "tennis") {
    return /\b(?:match\s*ended|retired|walkover|wo\b|final|finished)\b/i.test(s);
  }

  if (sportTab === "esports") {
    return /\b(?:map\s*ended|match\s*ended|final|finished|gg\b)\b/i.test(s);
  }

  return (
    /\b(?:final|finished|ended|full[\s-]?time|match\s*ended)\b/i.test(s) &&
    !/\b(?:not\s*started|prematch|scheduled|live)\b/i.test(s)
  );
}

function finalLabelForSport(sportTab) {
  if (sportTab === "football") return "FT";
  if (sportTab === "basketball") return "Final";
  if (sportTab === "tennis") return "Final";
  if (sportTab === "esports") return "Final";
  return null;
}

function inferFootballPhaseDetailed(norm) {
  const s = norm.statusBlob;
  if (!s) return "unknown";

  if (
    /\b(?:half[\s-]?time|halftime|half\s*time|interval|break\s*\(?(?:ht|half)|\bht\b(?!\s*\d)|between\s*halves)\b/i.test(
      s
    )
  ) {
    return "halftime";
  }
  if (/\b(?:penalt(?:y|ies)|pen\s*shootout|pens\b|shootout)\b/i.test(s)) return "penalties";
  if (/\b(?:extra\s*time|additional\s*time|supplementary)\b/i.test(s) || /\baet\b/i.test(s)) {
    return "extra_time";
  }
  if (/\b(?:2nd|second)\s*half\b/i.test(s) || /(?:^|[\s,/])(?:2h|h2)(?:[\s,/]|$)/i.test(s)) {
    return "second_half";
  }
  if (/\b(?:1st|first)\s*half\b/i.test(s) || /(?:^|[\s,/])(?:1h|h1)(?:[\s,/]|$)/i.test(s)) {
    return "first_half";
  }

  const p = norm.periodObj;
  if (p) {
    const name = String(p.name || p.type || p.label || p.code || "").toLowerCase();
    const n = Number(p.number ?? p.index ?? p.id);
    if (name.includes("half") && /2|second|ii|2nd/.test(name)) return "second_half";
    if (name.includes("half") && /1|first|i|1st/.test(name)) return "first_half";
    if (name.includes("interval") || name.includes("halftime")) return "halftime";
    if (name.includes("extra") || name.includes("overtime") || n === 3 || n === 4) {
      if (/\bet\b|extra/i.test(name)) return "extra_time";
    }
    if (n === 2 && !name.includes("quarter")) return "second_half";
    if (n === 1 && !name.includes("quarter")) return "first_half";
  }

  return "unknown";
}

function parseBasketballQuarterFromNorm(norm) {
  const s = norm.statusBlob;
  if (/\b(?:overtime|\bot\b|ot\s*\d)\b/i.test(s)) return "OT";
  if (/\b(?:half[\s-]?time|halftime|2nd\s*quarter\s*end|end\s*of\s*2nd)\b/i.test(s)) return "HT";

  const topQ = Number(norm.itemTopQuarter);
  if (topQ >= 1 && topQ <= 4) return topQ;

  const p = norm.periodObj;
  if (p) {
    const name = String(p.name || p.type || p.label || "").toLowerCase();
    const mq = /(?:q|quarter)\s*([1-4])|([1-4])(?:st|nd|rd|th)\s*quarter/.exec(name);
    if (mq) {
      const q = parseInt(mq[1] || mq[2], 10);
      if (q >= 1 && q <= 4) return q;
    }
    const n = Number(p.number ?? p.index ?? p.quarter ?? p.currentQuarter ?? p.period ?? p.id);
    if (n >= 1 && n <= 4) return n;
  }

  const sb = norm.sb;
  if (sb && typeof sb === "object") {
    const n = Number(sb.quarter ?? sb.period ?? sb.periodNumber);
    if (n >= 1 && n <= 4) return n;
    const sn = String(sb.periodName || sb.quarterName || "").toLowerCase();
    const mq2 = /(?:q|quarter)\s*([1-4])/.exec(sn);
    if (mq2) return parseInt(mq2[1], 10);
  }

  const live = norm.live;
  if (live && typeof live === "object") {
    const n = Number(live.quarter ?? live.currentQuarter ?? live.period ?? live.periodNumber);
    if (n >= 1 && n <= 4) return n;
  }
  const timer = norm.timer;
  if (timer && typeof timer === "object") {
    const n = Number(timer.quarter ?? timer.currentQuarter ?? timer.period ?? timer.periodNumber);
    if (n >= 1 && n <= 4) return n;
  }
  const gs = norm.gs && typeof norm.gs === "object" ? norm.gs : null;
  if (gs) {
    const n = Number(gs.quarter ?? gs.currentQuarter ?? gs.period ?? gs.periodNumber);
    if (n >= 1 && n <= 4) return n;
  }

  if (/\b(?:1st|first)\s*quarter\b/i.test(s) || /\bq\s*1\b/i.test(s)) return 1;
  if (/\b(?:2nd|second)\s*quarter\b/i.test(s) || /\bq\s*2\b/i.test(s)) return 2;
  if (/\b(?:3rd|third)\s*quarter\b/i.test(s) || /\bq\s*3\b/i.test(s)) return 3;
  if (/\b(?:4th|fourth)\s*quarter\b/i.test(s) || /\bq\s*4\b/i.test(s)) return 4;

  if (
    norm.periodPrimitive != null &&
    norm.periodPrimitive >= 1 &&
    norm.periodPrimitive <= 4
  ) {
    return norm.periodPrimitive;
  }

  return null;
}

function parseBasketballGameClockMmSs(raw) {
  const t = String(raw || "").trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return null;
  const mm = parseInt(m[1], 10);
  const ss = parseInt(m[2], 10);
  if (!Number.isFinite(mm) || !Number.isFinite(ss)) return null;
  if (ss < 0 || ss > 59) return null;
  // NCAA halves can be 20:00, NBA quarters 12:00, etc.
  if (mm > 20) return null;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function firstBasketballClockString(norm) {
  const { sb, live, timer, periodObj } = norm;
  const candidates = [
    norm.itemTopClock,
    sb?.clock,
    live?.clock,
    timer?.clock,
    periodObj?.clock,
    norm.gs && typeof norm.gs === "object" ? norm.gs.clock : null
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (c == null || c === "") continue;
    const parsed = parseBasketballGameClockMmSs(c);
    if (parsed) return parsed;
  }
  return null;
}

function formatBasketballLiveDisplay(norm, item, genericLabel) {
  const s = norm.statusBlob;
  if (isClearlyFinishedForSport(s, "basketball")) return "Final";

  const q = parseBasketballQuarterFromNorm(norm);
  if (q === "HT") return "HT";
  if (q === "OT") return "OT";

  const clock = firstBasketballClockString(norm);
  if (q != null && typeof q === "number" && clock) {
    return `Q${q} ${clock}`;
  }
  if (q != null && typeof q === "number") {
    return `Q${q}`;
  }

  const inferred = inferBasketballQuarterFromMarketScopeText(item, norm);
  if (inferred === "HT") return "HT";
  if (inferred === "OT") return "OT";
  if (typeof inferred === "number") {
    if (clock) return `Q${inferred} ${clock}`;
    return `Q${inferred}`;
  }

  // No reliable quarter → let caller fall back (e.g. generic LIVE).
  return null;
}

function inferTennisSetNumber(norm, item) {
  const candidates = [
    item?.currentSet,
    item?.setNumber,
    item?.set,
    item?.period,
    item?.current_period,
    item?.currentPeriod,
    item?.current_set,
    norm.periodObj?.set,
    norm.periodObj?.number,
    norm.periodObj?.period,
    norm.periodObj?.setNumber,
    norm.periodPrimitive,
    norm.sb?.currentSet,
    norm.sb?.set,
    norm.sb?.setNumber,
    norm.sb?.period,
    norm.sb?.periodNumber,
    norm.live?.set,
    norm.live?.currentSet,
    norm.live?.setNumber,
    norm.timer?.set,
    norm.timer?.currentSet,
    norm.timer?.setNumber,
    norm.gs && typeof norm.gs === "object" ? norm.gs.currentSet ?? norm.gs.set : null
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    const n = Number(v);
    if (Number.isFinite(n) && n >= 1 && n <= 9) return n;
  }
  const s = norm.statusBlob;
  const m = /(?:set|sets)\s*([1-9])\b/i.exec(s);
  if (m) return parseInt(m[1], 10);
  return null;
}

function trySafeTennisGameScore(norm, item) {
  const g =
    item?.currentGameScore ??
    item?.gameScore ??
    norm.sb?.currentGameScore ??
    norm.sb?.gameScore ??
    (norm.gs && typeof norm.gs === "object" ? norm.gs.currentGameScore ?? norm.gs.gameScore : null);

  if (g == null || g === "") return null;
  if (typeof g === "string") {
    const t = g.trim();
    if (/^\d{1,2}\s*-\s*\d{1,2}$/.test(t)) return t.replace(/\s*-\s*/, "-");
    return null;
  }
  if (typeof g === "object") {
    const home = g.home ?? g.h ?? g[0];
    const away = g.away ?? g.a ?? g[1];
    const hn = Number(home);
    const an = Number(away);
    if (Number.isFinite(hn) && Number.isFinite(an) && hn >= 0 && hn <= 7 && an >= 0 && an <= 7) {
      return `${hn}-${an}`;
    }
  }
  return null;
}

function formatTennisLiveDisplay(norm, item, genericLabel) {
  const s = norm.statusBlob;
  if (isClearlyFinishedForSport(s, "tennis")) return "Final";

  const setN = inferTennisSetNumber(norm, item);
  if (setN != null) {
    const gs = trySafeTennisGameScore(norm, item);
    if (gs) return `Set ${setN} · ${gs}`;
    return `Set ${setN}`;
  }

  const setFromMarket = inferTennisSetNumberFromMarketScopeText(item, norm);
  if (setFromMarket != null) return `Set ${setFromMarket}`;

  // No reliable set scope → let caller fall back (e.g. generic LIVE).
  return null;
}

function inferEsportsMapNumber(norm, item) {
  const candidates = [
    item?.currentMap,
    item?.mapNumber,
    item?.map,
    item?.gameNumber,
    item?.game,
    item?.period,
    item?.current_period,
    item?.currentPeriod,
    norm.periodObj?.map,
    norm.periodObj?.number,
    norm.periodObj?.period,
    norm.periodPrimitive,
    norm.sb?.map,
    norm.sb?.currentMap,
    norm.sb?.mapNumber,
    norm.sb?.period,
    norm.live?.map,
    norm.live?.currentMap,
    norm.live?.mapNumber,
    norm.timer?.map,
    norm.timer?.currentMap,
    norm.timer?.mapNumber,
    norm.gs && typeof norm.gs === "object" ? norm.gs.map ?? norm.gs.currentMap : null
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (v == null || v === "") continue;
    if (typeof v === "number" && Number.isFinite(v) && v >= 1 && v <= 99) return v;
    if (typeof v === "string") {
      const t = v.trim();
      const dm = /^(\d{1,2})\s*$/.exec(t);
      if (dm) {
        const n = parseInt(dm[1], 10);
        if (n >= 1 && n <= 99) return n;
      }
      const mm = /(?:map|game)\s*#?\s*(\d{1,2})/i.exec(t);
      if (mm) {
        const n = parseInt(mm[1], 10);
        if (n >= 1 && n <= 99) return n;
      }
    }
  }
  const s = norm.statusBlob;
  const m = /(?:^|[\s,/])(?:map|game)\s*(\d{1,2})(?:[\s,/]|$)/i.exec(s);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 99) return n;
  }
  return null;
}

function esportsOvertimeClearly(norm) {
  return /\b(?:overtime|\bot\b|o\.?t\.?)\b/i.test(norm.statusBlob);
}

function formatEsportsLiveDisplay(norm, item, genericLabel) {
  const s = norm.statusBlob;
  if (isClearlyFinishedForSport(s, "esports")) return "Final";

  const mapN = inferEsportsMapNumber(norm, item);
  if (mapN != null) {
    if (esportsOvertimeClearly(norm)) return `Map ${mapN} · OT`;
    return `Map ${mapN}`;
  }

  const mapFromMarket = inferEsportsMapNumberFromMarketScopeText(item, norm);
  if (mapFromMarket != null) {
    if (esportsOvertimeClearly(norm)) return `Map ${mapFromMarket} · OT`;
    return `Map ${mapFromMarket}`;
  }

  // No reliable map scope → let caller fall back (e.g. generic LIVE).
  return null;
}

function formatFootballLiveDisplay(norm, item, genericLabel) {
  const s = norm.statusBlob;
  if (isClearlyFinishedForSport(s, "football")) return "FT";

  const phase = inferFootballPhaseDetailed(norm);

  if (phase === "halftime") return "HT";
  if (phase === "penalties") return "PEN";

  const mainMin = (() => {
    const { fx, ev, sb, live, timer, periodObj } = norm;
    const minuteCandidates = [
      item?.minute,
      item?.matchMinute,
      item?.liveMinute,
      item?.elapsedMinutes,
      sb?.minute,
      fx?.minute,
      ev?.minute,
      live?.minute,
      timer?.minute,
      periodObj?.minute,
      norm.gs && typeof norm.gs === "object" ? norm.gs.minute : null
    ];
    for (let i = 0; i < minuteCandidates.length; i += 1) {
      const m = tryMinuteFromUnknown(minuteCandidates[i]);
      if (m != null) return m;
    }
    return null;
  })();

  const stoppage = (() => {
    const { fx, ev, live, timer, periodObj } = norm;
    const stCandidates = [
      item?.stoppage,
      item?.added,
      item?.injuryTime,
      fx?.stoppage,
      ev?.stoppage,
      live?.stoppage,
      timer?.stoppage,
      periodObj?.stoppage
    ];
    for (let i = 0; i < stCandidates.length; i += 1) {
      const st = tryStoppageFromUnknown(stCandidates[i]);
      if (st != null && st > 0) return st;
    }
    return null;
  })();

  const clockCore = formatLiveClockFromParts(mainMin, stoppage);
  const generic = genericLabel;

  if (phase === "extra_time") {
    if (clockCore) {
      if (/\baet\b/i.test(s)) return `AET ${clockCore}`;
      return `ET ${clockCore}`;
    }
    const g = generic != null ? String(generic).trim() : "";
    if (g && /^\d{1,3}(?:\+\d{1,2})?'$/.test(g)) {
      return /\baet\b/i.test(s) ? `AET ${g}` : `ET ${g}`;
    }
    return /\baet\b/i.test(s) ? "AET" : "ET";
  }

  if (phase === "second_half") {
    if (clockCore) return `2H ${clockCore}`;
    const g = generic != null ? String(generic).trim() : "";
    if (g && /^\d{1,3}(?:\+\d{1,2})?'$/.test(g)) return `2H ${g}`;
    return generic || clockCore;
  }

  if (phase === "first_half") {
    if (clockCore) return clockCore;
    return generic;
  }

  if (phase === "unknown") {
    if (clockCore) return clockCore;
    return generic;
  }

  return generic;
}

function tryElapsedObjectParts(obj) {
  if (!obj || typeof obj !== "object") return null;
  const main = tryMinuteFromUnknown(
    obj.minute ?? obj.min ?? obj.m ?? obj.matchMinute ?? obj.elapsedMinutes
  );
  if (main == null) return null;
  const stop =
    tryStoppageFromUnknown(
      obj.stoppage ??
        obj.added ??
        obj.injuryTime ??
        obj.addedTime ??
        obj.extraMinute ??
        obj.extraMinutes ??
        obj.injuryMinutes ??
        obj.additionalMinutes
    ) ?? 0;
  return formatLiveClockFromParts(main, stop);
}

function buildGenericLiveClockLabelFromItem(item) {
  const { fx, ev, sb, live, timer, period } = gatherNestedObjects(item);

  const stringCandidates = [
    [item?.matchTimeLabel, false],
    [item?.liveTimeLabel, false],
    [item?.eventTimeLabel, false],
    [item?.matchClock, true],
    [item?.matchTime, false],
    [item?.liveTime, false],
    [sb?.clock, true],
    [sb?.minute, false],
    [live?.clock, true],
    [timer?.clock, true],
    [period?.clock, true],
    [item?.clock, true],
    [fx?.clock, true],
    [fx?.statusLabel, false],
    [ev?.clock, true],
    [ev?.statusLabel, false],
    [typeof fx?.status === "string" ? fx.status : null, false],
    [typeof ev?.status === "string" ? ev.status : null, false],
    [item?.statusLabel, false],
    [item?.time, false]
  ];

  for (let i = 0; i < stringCandidates.length; i += 1) {
    const val = stringCandidates[i][0];
    const allowMmSs = stringCandidates[i][1];
    if (val == null || val === "") continue;
    const parsedStr = parseLiveClockLabel(val);
    if (parsedStr) return parsedStr;
    if (allowMmSs && typeof val === "string") {
      const mmss = tryParseMmSsMatchClock(val);
      if (mmss) return mmss;
    }
  }

  const elapsedObjects = [
    item?.elapsed,
    fx?.elapsed,
    ev?.elapsed,
    live?.elapsed,
    timer?.elapsed,
    period?.elapsed
  ];
  for (let i = 0; i < elapsedObjects.length; i += 1) {
    const got = tryElapsedObjectParts(elapsedObjects[i]);
    if (got) return got;
  }

  const minuteCandidates = [
    item?.minute,
    item?.matchMinute,
    item?.liveMinute,
    item?.elapsedMinutes,
    sb?.minute,
    fx?.minute,
    ev?.minute,
    live?.minute,
    timer?.minute,
    period?.minute
  ];

  if (item?.elapsed != null && typeof item.elapsed === "number") {
    minuteCandidates.push(item.elapsed);
  }
  if (fx?.elapsed != null && typeof fx.elapsed === "number") minuteCandidates.push(fx.elapsed);
  if (ev?.elapsed != null && typeof ev.elapsed === "number") minuteCandidates.push(ev.elapsed);

  let mainMin = null;
  for (let i = 0; i < minuteCandidates.length; i += 1) {
    const m = tryMinuteFromUnknown(minuteCandidates[i]);
    if (m != null) {
      mainMin = m;
      break;
    }
  }

  const stoppageCandidates = [
    item?.stoppage,
    item?.added,
    item?.injuryTime,
    item?.addedTime,
    item?.extraMinute,
    item?.extraMinutes,
    item?.injuryMinutes,
    item?.additionalMinutes,
    fx?.stoppage,
    fx?.added,
    fx?.injuryTime,
    fx?.addedTime,
    ev?.stoppage,
    ev?.added,
    live?.stoppage,
    timer?.stoppage,
    period?.stoppage
  ];

  let stop = null;
  for (let i = 0; i < stoppageCandidates.length; i += 1) {
    const st = tryStoppageFromUnknown(stoppageCandidates[i]);
    if (st != null && st > 0) {
      stop = st;
      break;
    }
  }

  if (mainMin != null) {
    return formatLiveClockFromParts(mainMin, stop);
  }

  return null;
}

function buildLiveClockLabelFromItem(item) {
  const norm = readNormalizedLiveState(item);
  const generic = buildGenericLiveClockLabelFromItem(item);
  const sportTab = analysisSportTabForItem(item);

  const fin = finalLabelForSport(sportTab);
  if (fin && isClearlyFinishedForSport(norm.statusBlob, sportTab)) {
    return fin;
  }

  if (sportTab === "football") {
    const v = formatFootballLiveDisplay(norm, item, generic);
    if (v) return v;
  } else if (sportTab === "basketball") {
    const v = formatBasketballLiveDisplay(norm, item, generic);
    if (v != null) return v;
    return null;
  } else if (sportTab === "tennis") {
    const v = formatTennisLiveDisplay(norm, item, generic);
    if (v != null) return v;
    return null;
  } else if (sportTab === "esports") {
    const v = formatEsportsLiveDisplay(norm, item, generic);
    if (v != null) return v;
    return null;
  }

  // Other sports: fall back to generic live clock if any.
  return generic;
}

export function getAnalysisStatusLabel(
  item,
  t,
  lang = ANALYSIS_DEFAULT_LANG,
  fallbackKickoff = "—",
  explicitTimeZone
) {
  const norm = readNormalizedLiveState(item);
  const sportTab = analysisSportTabForItem(item);
  const rawStatus = String(
    item?.status ?? item?.matchStatus ?? item?.state ?? item?.phase ?? item?.scope ?? ""
  ).toLowerCase();

  const textSaysLive =
    rawStatus.includes("live") ||
    rawStatus.includes("inplay") ||
    rawStatus.includes("in-play") ||
    rawStatus.includes("playing");

  const hasAnyLiveObject =
    (norm.sb && typeof norm.sb === "object") ||
    (norm.live && typeof norm.live === "object") ||
    (norm.timer && typeof norm.timer === "object") ||
    (norm.periodObj && typeof norm.periodObj === "object") ||
    (norm.gs && typeof norm.gs === "object");

  const hasFootballMinuteEvidence = (() => {
    if (sportTab !== "football") return false;
    const minuteCandidates = [
      item?.minute,
      item?.matchMinute,
      item?.liveMinute,
      item?.elapsedMinutes,
      norm.sb?.minute,
      norm.live?.minute,
      norm.timer?.minute,
      norm.periodObj?.minute,
      norm.gs && typeof norm.gs === "object" ? norm.gs.minute : null
    ];
    for (let i = 0; i < minuteCandidates.length; i += 1) {
      const m = tryMinuteFromUnknown(minuteCandidates[i]);
      if (m != null) return true;
    }
    return false;
  })();

  const hasBasketballQuarterEvidence =
    sportTab === "basketball" && parseBasketballQuarterFromNorm(norm) != null;

  const hasTennisSetEvidence = sportTab === "tennis" && inferTennisSetNumber(norm, item) != null;

  const hasEsportsMapEvidence = sportTab === "esports" && inferEsportsMapNumber(norm, item) != null;

  const strongSportEvidence =
    hasFootballMinuteEvidence ||
    hasBasketballQuarterEvidence ||
    hasTennisSetEvidence ||
    hasEsportsMapEvidence;

  const isLive =
    item?.isLive === true ||
    item?.scope === "live" ||
    textSaysLive ||
    (hasAnyLiveObject && strongSportEvidence);

  // Resolve timezone once so both prematch date & time parts share the exact
  // same timezone resolution path.
  const resolvedTimeZone = getAnalysisTimeZone(item, explicitTimeZone);

  const kickoffDisplay = getKickoffDisplay(item, lang, fallbackKickoff, resolvedTimeZone);

  if (isLive) {
    const liveClock = buildLiveClockLabelFromItem(item);
    const timeValue = liveClock != null ? liveClock : "LIVE";
    return {
      badge: t?.common?.live || "Live",
      timeValue,
      isLive: true
    };
  }

  const prematchDateStr = formatAnalysisPrematchDate(item, lang, resolvedTimeZone);

  let timeValue = "—";
  if (prematchDateStr === "—" && kickoffDisplay === "—") {
    timeValue = "—";
  } else if (prematchDateStr === "—" && kickoffDisplay !== "—") {
    timeValue = kickoffDisplay;
  } else if (prematchDateStr !== "—" && kickoffDisplay === "—") {
    timeValue = prematchDateStr;
  } else {
    timeValue = `${prematchDateStr} • ${kickoffDisplay}`;
  }

  return {
    badge: t?.common?.prematch || "Prematch",
    timeValue,
    isLive: false
  };
}
